import { useState } from 'react';
import { CheckCircle, Clock, XCircle, FileWarning, FileText } from 'lucide-react';
import { FileUpload } from '@/components/shared/file-upload';
import type { FileValue } from '@/components/shared/use-file-upload';
import { formatDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { useUploadCompetencyEvidence, type MyEvidenceActivity } from '@/api/competency-evidence';

type Tone = 'green' | 'amber' | 'red' | 'gray';

const TONE: Record<Tone, { badge: string; border: string }> = {
  green: { badge: 'bg-green-100 text-green-700', border: 'border-green-300' },
  amber: { badge: 'bg-amber-100 text-amber-700', border: 'border-amber-300' },
  red: { badge: 'bg-red-100 text-red-700', border: 'border-red-300' },
  gray: { badge: 'bg-gray-100 text-gray-600', border: 'border-gray-300' },
};

function derive(a: MyEvidenceActivity): { label: string; tone: Tone; icon: typeof Clock } {
  const up = a.evidence_uploads[0];
  if (!up) return { label: 'Sin cargar', tone: 'amber', icon: FileWarning };
  if (up.status === 'rejected') return { label: 'Rechazado', tone: 'red', icon: XCircle };
  if (up.status === 'pending')
    return { label: 'Pendiente de aprobación', tone: 'amber', icon: Clock };
  if (up.expires_at && new Date(up.expires_at) < new Date())
    return { label: 'Vencido', tone: 'gray', icon: Clock };
  return { label: 'Aprobado', tone: 'green', icon: CheckCircle };
}

export function EvidenceActivityCard({ activity }: { activity: MyEvidenceActivity }) {
  const upload = activity.evidence_uploads[0];
  const status = derive(activity);
  const Icon = status.icon;
  const tone = TONE[status.tone];

  const isRejected = upload?.status === 'rejected';
  const isPending = upload?.status === 'pending';
  const isExpired =
    upload?.status === 'approved' &&
    !!upload.expires_at &&
    new Date(upload.expires_at) < new Date();
  const approvedValid = upload?.status === 'approved' && !isExpired;

  const [replacing, setReplacing] = useState(false);
  const uploadMut = useUploadCompetencyEvidence();

  // Show the uploader when there's nothing valid on file, or the colaborador chose
  // to replace it. A pending / approved-valid upload shows a summary instead.
  const showUploader = !upload || isRejected || isExpired || replacing;
  const uploaderLabel = replacing
    ? 'Reemplazar certificado'
    : isRejected
      ? 'Volver a subir'
      : isExpired
        ? 'Subir una versión vigente'
        : 'Subir tu certificado';

  const handleChange = (f: FileValue | null) => {
    if (!f) return;
    uploadMut.mutate(
      {
        activityId: activity.id,
        data: { file_url: f.url, file_name: f.name, file_size: f.size, file_type: f.type },
      },
      { onSuccess: () => setReplacing(false) },
    );
  };

  return (
    <div className={cn('rounded-xl border bg-card p-4', tone.border)}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-card-foreground">
            {activity.position_competency.name}
          </h3>
          <p className="text-xs text-muted-foreground">
            {activity.position_competency.position.name}
          </p>
        </div>
        <span
          className={cn(
            'flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
            tone.badge,
          )}
        >
          <Icon className="h-3.5 w-3.5" /> {status.label}
        </span>
      </div>

      {activity.instruction && (
        <p className="mt-2 text-xs text-muted-foreground">{activity.instruction}</p>
      )}

      {isRejected && upload?.review_notes && (
        <p className="mt-2 rounded-md bg-red-50 px-2 py-1 text-xs text-red-700">
          Motivo del rechazo: {upload.review_notes}
        </p>
      )}

      {/* Summary of the file already on record (pending or approved-valid). Stays
          visible even while replacing, so the colaborador never loses the context
          of their valid/submitted certificate — including on an upload error. */}
      {upload && (isPending || approvedValid) && (
        <div className="mt-3 rounded-lg border border-border bg-muted/20 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <a
              href={upload.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-w-0 items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary"
            >
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{upload.file_name}</span>
            </a>
            {!replacing && (
              <button
                onClick={() => {
                  uploadMut.reset();
                  setReplacing(true);
                }}
                className="shrink-0 cursor-pointer text-xs font-medium text-primary hover:text-primary/80"
              >
                Reemplazar
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {isPending
              ? 'Archivo enviado. En espera de aprobación del responsable.'
              : `Aprobado y vigente${
                  approvedValid && upload.expires_at
                    ? ` hasta el ${formatDate(upload.expires_at)}`
                    : ' de forma permanente'
                }.`}
          </p>
        </div>
      )}

      {showUploader && (
        <div className="mt-3">
          {replacing && (
            <button
              onClick={() => {
                uploadMut.reset();
                setReplacing(false);
              }}
              className="mb-1 cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              Cancelar
            </button>
          )}
          <FileUpload
            folder="certificates"
            accept=".pdf,.jpg,.jpeg,.png"
            maxSizeMB={50}
            onChange={handleChange}
            disabled={uploadMut.isPending}
            label={uploaderLabel}
          />
          {uploadMut.isError && (
            <p className="mt-1 text-xs text-red-700">
              No se pudo cargar la evidencia. Intenta de nuevo.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
